<script setup>
const props = defineProps({
    image: {
        type: Object,
        required: false,
        default: {},
    },
    src: {
        type: String,
        required: false,
        default: null,
    },
    alt: {
        type: String,
        required: false,
        default: null,
    },
    lazy: {
        type: Boolean,
        required: false,
        default: true,
    },
    quality: {
        type: Number,
        required: false,
        default: 50,
    },
    forceLoad: {
        type: Boolean,
        required: false,
        default: false,
    },
    height: {
        type: [Number, Boolean],
        required: false,
        default: false,
    },
    width: {
        type: [Number, Boolean],
        required: false,
        default: false,
    },
    mod: {
        type: Object,
        required: false,
        default: () => ({}),
    },
    sizes: {
        type: String,
        required: false,
        default: 'xs:319px sm:639px md:1023px lg:1279px xl:1439 xxl:1920px',
    },
    decoration: {
        type: Boolean,
        required: false,
        default: false,
    }
});

const loadingValue = props.lazy ? 'lazy' : 'eager';

// Setup URL
let url = props.image?.url || props.image?.src || props.src;

// Define URLs to remove:
// NuxtImage should resolve the correct URL for the environment
// based on the providers base URL
const config = useRuntimeConfig();
const { imageUrlsToRemove } = config.public;
const urlsToRemove = imageUrlsToRemove.split(',');

// Check if the URL contains any of the URLs to remove
const shouldRemove = urlsToRemove.some((url) => {
    return props.image?.url?.includes(url) || props.image?.src?.includes(url) || props.src?.includes(url);
});

if (shouldRemove) {
    // Remove the URL from the image src
    urlsToRemove.forEach((aUrl) => {
        url = url.replace(aUrl, '');
    });
}

const urlsToProxy = ['v5.airtableusercontent.com', 'cdn.builder.io', 'inbound.com'];
const proxyUrl = 'images.proxy.thisismess.io';
const shouldProxy = urlsToProxy.some((url) => {
    let shouldProxy = false;
    shouldProxy = shouldProxy || props.image?.url?.includes(url);
    shouldProxy = shouldProxy || props.image?.src?.includes(url);
    shouldProxy = shouldProxy || props.src?.includes(url);
    return shouldProxy;
});

if (shouldProxy) {
    urlsToProxy.forEach((aUrl) => {
        url = url.replace(aUrl, proxyUrl);
    });
}

const maxWidth = 1920;
let setWidth = props.width * 2 || maxWidth || null;
if (setWidth && setWidth > maxWidth) setWidth = maxWidth;

const baseModifier = {
    w: setWidth,
    q: props.quality,
    h: props.height || null,
    fit: 'max',
    fm: 'webp',
    fo: 'face',
    ...props.mod,
};
</script>

<template>
    <div class="mess-image">
        <nuxt-picture :loading="loadingValue" :preload="!lazy" :placeholder="lazy ? 25 : false" :src="url"
            :alt="!decoration && (alt || image.alt) || ''" format="webp" :sizes="sizes || null" :modifiers="{ ...baseModifier }" />
    </div>
</template>

<style lang="scss">
.mess-image {
    position: relative;

    picture {
        &.full-sized-image {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
        }

        display: block;
        width: 100%;
        height: 100%;

        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }
    }
}
</style>
